import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';
import {
  AddressCabinaSearchService,
  AddressSuggestion,
  CabinaByAddress,
} from '../../_services/address-cabina-search.service';

const DEBOUNCE_MS = 300;

export type AddressSearchMode = 'options' | 'address';

/**
 * Stato per-istanza della ricerca cabina per indirizzo, condiviso da
 * SelectFieldType e MultiSelectFieldType. Niente `providedIn`: va messo
 * nei `providers` del field type così ogni campo ha il proprio stato.
 */
@Injectable()
export class AddressSearchController {
  private readonly _search = inject(AddressCabinaSearchService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly mode = signal<AddressSearchMode>('options');
  readonly query = signal('');
  readonly suggestions = signal<AddressSuggestion[]>([]);
  readonly loading = signal(false);
  readonly matched = signal<CabinaByAddress | null>(null);
  /** true quando google.maps.places non è disponibile: mostra il messaggio nel pannello. */
  readonly placesUnavailable = signal(false);

  /** true quando l'indirizzo è stato risolto: la lista va filtrata (anche a zero risultati). */
  readonly active = computed(() => this.mode() === 'address' && this._resolved());
  // 🔥 esposto al pannello per non mostrare "nessun indirizzo trovato" dopo che
  // un suggerimento è già stato risolto (i suggerimenti sono [] anche in quel caso).
  // computed() anziché _resolved.asReadonly(): _resolved è dichiarato più sotto
  // in questa classe e un field initializer verrebbe eseguito prima di lui.
  readonly resolved = computed(() => this._resolved());

  private readonly _resolved = signal(false);
  private readonly _query$ = new Subject<string>();
  private readonly _suggestion$ = new Subject<AddressSuggestion>();

  constructor() {
    this._query$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(() => {
          this._resolved.set(false);
          this.matched.set(null);
        }),
        // switchMap: le richieste di autocomplete si cancellano a vicenda
        switchMap((q) => this._search.suggest(q)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((items) => this.suggestions.set(items));

    this._suggestion$
      .pipe(
        tap(() => this.loading.set(true)),
        // switchMap: click rapidi consecutivi su suggerimenti diversi si cancellano a vicenda
        switchMap((item) => this._search.resolveCabina(item.placeId)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((cabina) => {
        this.matched.set(cabina);
        // 🔥 anche con cabina null: lista vuota → empty state standard del dropdown
        this._resolved.set(true);
        this.loading.set(false);
      });
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.placesUnavailable.set(value.trim().length >= 3 && !this._search.hasMapsLoader());
    this._query$.next(value);
  }

  selectSuggestion(item: AddressSuggestion): void {
    this.query.set(item.descrizione);
    this.suggestions.set([]);
    this._suggestion$.next(item);
  }

  setMode(mode: AddressSearchMode): void {
    this.mode.set(mode);
    this.reset();
  }

  reset(): void {
    this.query.set('');
    this.suggestions.set([]);
    this.matched.set(null);
    this._resolved.set(false);
    this.loading.set(false);
    this.placesUnavailable.set(false);
  }

  /** Filtra le options sulla cabina risolta; array vuoto se nessuna corrispondenza. */
  filterOptions<T extends Record<string, unknown>>(options: T[], matchKeys: string[]): T[] {
    const cabina = this.matched();
    if (!cabina) return [];
    const target: Record<string, unknown> = { id: cabina.id, cod_ac: cabina.cod_ac };
    return options.filter((option) =>
      matchKeys.some((key) => {
        const expected = target[key];
        const actual = option[key];
        // confronto su stringa: gli id cabina sono number lato GraphQL ma in
        // qualche option sono normalizzati a stringa
        return expected != null && actual != null && String(actual) === String(expected);
      }),
    );
  }
}
